import { useRef, useEffect, useState } from 'react'
import { Button, Col, Dropdown, Form, Modal, Row, Spinner } from 'react-bootstrap'
import LastLocation from './LastLocation'
import moment from 'moment'
import { userprofile } from '../../const'
import { useNavigate } from 'react-router-dom'
import NotificationBell from '../../Components/NotificationBell'
import { useDispatch } from 'react-redux'
import { blockUser, getBlockStatus, renameCustomer, reportUser } from '../../reducers/commonReducer'

const REPORT_REASONS = [
  'Spam',
  'Harassment',
  'Inappropriate content',
  'Fake account',
  'Other',
];

export default function MobileHeader({
  logo, name, time, agent,
  showSearch, onSearchToggle,
  searchQuery, setSearchQuery,
  searchCount, searchMatchesLength,
  onSearchPrev, onSearchNext,
  customerId, partnerRoleSlug,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const slug = localStorage.getItem("slug");
  const isTeamLeader = slug === "team_leader";
  const isTestBranch = localStorage.getItem("branchName")?.toLowerCase() === "testbranch";

  const [editMode, setEditMode] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState(name);

  // Block state
  const [isBlocked, setIsBlocked] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  // Report state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => { setDisplayName(name); }, [name]);

  // Fetch block status when chatting with someone (testbranch only)
  useEffect(() => {
    if (!isTestBranch || !customerId) return;
    dispatch(getBlockStatus(customerId)).then((res) => {
      if (res?.payload?.status === 1) setIsBlocked(res.payload.blocked);
    });
  }, [customerId, isTestBranch, dispatch]);

  const openEdit = () => {
    const parts = (displayName || "").trim().split(" ");
    setFirstName(parts[0] || "");
    setLastName(parts.slice(1).join(" ") || "");
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!firstName.trim()) return;
    setSaving(true);
    const result = await dispatch(renameCustomer({
      id: customerId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    }));
    setSaving(false);
    if (result?.payload?.status) {
      setDisplayName(`${firstName.trim()} ${lastName.trim()}`.trim());
      setEditMode(false);
    }
  };

  const handleBlockConfirm = async () => {
    setBlockLoading(true);
    const res = await dispatch(blockUser(customerId));
    setBlockLoading(false);
    if (res?.payload?.status === 1) {
      setIsBlocked(res.payload.blocked);
    }
    setShowBlockModal(false);
  };

  const handleReportSubmit = async () => {
    if (!reportReason) return;
    setReportLoading(true);
    await dispatch(reportUser({ targetId: customerId, reason: reportReason, description: reportDesc }));
    setReportLoading(false);
    setShowReportModal(false);
    setReportReason('');
    setReportDesc('');
  };

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  if (showSearch) {
    return (
      <Row className="bg-themesecond shadow-none p-2 rounded align-items-center">
        <Col xs={1} className="px-1">
          <Button variant="link" className="p-0 text-dark" onClick={onSearchToggle} aria-label="Close search">
            <i className="bi bi-arrow-left" style={{ fontSize: 20 }}></i>
          </Button>
        </Col>
        <Col xs={7} className="px-1">
          <Form.Control
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages..."
            className="chat-search-input border-0 shadow-none bg-transparent"
            style={{ fontSize: 15 }}
          />
        </Col>
        <Col xs={2} className="px-0 text-center">
          <span style={{ fontSize: 13, color: '#6b7280' }}>{searchCount}</span>
        </Col>
        <Col xs={1} className="px-0 text-center">
          <Button variant="link" className="p-0 text-dark" onClick={onSearchPrev} disabled={!searchMatchesLength} aria-label="Previous">
            <i className="bi bi-chevron-up"></i>
          </Button>
        </Col>
        <Col xs={1} className="px-0 text-center">
          <Button variant="link" className="p-0 text-dark" onClick={onSearchNext} disabled={!searchMatchesLength} aria-label="Next">
            <i className="bi bi-chevron-down"></i>
          </Button>
        </Col>
      </Row>
    );
  }

  const showMoreMenu = isTestBranch && !!customerId;

  return (
    <>
      <Row className="bg-themesecond shadow-none p-2 rounded align-items-center">
        <Col md={1} sm={1} xs={1}>
          <h4 className="mb-0">
            <LastLocation />
          </h4>
        </Col>
        <Col md={2} sm={2} xs={2}>
          <img className="profile-img" src={logo} alt={name} />
        </Col>
        <Col
          md={localStorage.getItem("slug") !== "customer" && !agent ? (showMoreMenu ? 5 : 6) : showMoreMenu ? 6 : 7}
          sm={localStorage.getItem("slug") !== "customer" && !agent ? (showMoreMenu ? 5 : 6) : showMoreMenu ? 6 : 7}
          xs={localStorage.getItem("slug") !== "customer" && !agent ? (showMoreMenu ? 5 : 6) : showMoreMenu ? 6 : 7}
        >
          {isTeamLeader && customerId && editMode ? (
            <div className="d-flex align-items-center gap-1 flex-wrap">
              <Form.Control
                size="sm"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ width: 90 }}
                autoFocus
              />
              <Form.Control
                size="sm"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ width: 90 }}
              />
              <Button size="sm" variant="success" onClick={handleSave} disabled={saving || !firstName.trim()}>
                {saving ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check-lg"></i>}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditMode(false)} disabled={saving}>
                <i className="bi bi-x-lg"></i>
              </Button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <div>
                <p className="mb-0 d-flex align-items-center gap-1">
                  <b>{displayName}</b>
                  {(partnerRoleSlug === "team_leader" || partnerRoleSlug === "branch_leader") && (
                    <span className="verified-badge" style={{ position: "relative", bottom: "auto", right: "auto", width: 22, height: 22, display: "inline-flex" }}>
                      <i className="bi bi-patch-check-fill" style={{ fontSize: 20, color: "#25d366" }}></i>
                    </span>
                  )}
                </p>
                {time && <p className="mb-0">Last active {moment(time).format("DD MMM hh:mm A")}</p>}
              </div>
              {isTeamLeader && customerId && (
                <i
                  className="bi bi-pencil-fill"
                  style={{ fontSize: 13, cursor: "pointer", color: "#555" }}
                  onClick={openEdit}
                  title="Edit customer name"
                ></i>
              )}
            </div>
          )}
        </Col>
        <Col md={1} sm={1} xs={1} className="px-0 text-end">
          <Button variant="link" className="p-0 text-dark" onClick={onSearchToggle} aria-label="Search">
            <i className="bi bi-search" style={{ fontSize: 18 }}></i>
          </Button>
        </Col>
        <Col md={1} sm={1} xs={1} className="px-0 text-end">
          <NotificationBell />
        </Col>
        {(localStorage.getItem("slug") !== "customer") && !agent && (
          <Col md={1} sm={1} xs={1} className="px-0" onClick={() => navigate(userprofile)}>
            <Button variant="info" className="px-1">
              <i className="bi bi-pen-fill"></i>
            </Button>
          </Col>
        )}
        {showMoreMenu && (
          <Col md={1} sm={1} xs={1} className="px-0 text-end">
            <Dropdown align="end">
              <Dropdown.Toggle
                as="button"
                className="btn btn-link p-0 text-dark border-0 shadow-none"
                aria-label="More options"
                style={{ background: 'none' }}
              >
                <i className="bi bi-three-dots-vertical" style={{ fontSize: 20 }}></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setShowBlockModal(true)}>
                  <i className={`bi ${isBlocked ? 'bi-person-check' : 'bi-slash-circle'} me-2`}></i>
                  {isBlocked ? 'Unblock' : 'Block'}
                </Dropdown.Item>
                <Dropdown.Item className="text-danger" onClick={() => setShowReportModal(true)}>
                  <i className="bi bi-flag me-2"></i>
                  Report
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        )}
      </Row>

      {/* Block Confirmation Modal */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} centered size="sm">
        <Modal.Body className="p-4 text-center">
          <div className="mb-3">
            <i className="bi bi-slash-circle text-danger" style={{ fontSize: 40 }}></i>
          </div>
          <h6 className="fw-bold mb-2">
            {isBlocked ? `Unblock ${name}?` : `Block ${name}?`}
          </h6>
          <p className="text-muted mb-4" style={{ fontSize: 13 }}>
            {isBlocked
              ? `${name} will be able to send you messages again.`
              : `Blocked users won't be able to send you messages. They won't be notified that you blocked them.`}
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="outline-secondary" size="sm" onClick={() => setShowBlockModal(false)} disabled={blockLoading}>
              Cancel
            </Button>
            <Button
              variant={isBlocked ? "success" : "danger"}
              size="sm"
              onClick={handleBlockConfirm}
              disabled={blockLoading}
            >
              {blockLoading
                ? <Spinner animation="border" size="sm" />
                : isBlocked ? "Unblock" : "Block"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Report Modal */}
      <Modal show={showReportModal} onHide={() => { setShowReportModal(false); setReportReason(''); setReportDesc(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Report {name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Your report is anonymous. Select a reason and optionally add more details.
          </p>
          <div className="mb-3">
            {REPORT_REASONS.map((r) => (
              <div
                key={r}
                className={`p-2 mb-2 rounded border d-flex align-items-center gap-2`}
                style={{ cursor: 'pointer', background: reportReason === r ? '#fff3cd' : '#fff', borderColor: reportReason === r ? '#ffc107' : '#dee2e6' }}
                onClick={() => setReportReason(r)}
              >
                <i className={`bi ${reportReason === r ? 'bi-check-circle-fill text-warning' : 'bi-circle'}`}></i>
                <span style={{ fontSize: 14 }}>{r}</span>
              </div>
            ))}
          </div>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Add details (optional)"
            value={reportDesc}
            onChange={(e) => setReportDesc(e.target.value)}
            style={{ fontSize: 13 }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => { setShowReportModal(false); setReportReason(''); setReportDesc(''); }} disabled={reportLoading}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleReportSubmit} disabled={!reportReason || reportLoading}>
            {reportLoading ? <Spinner animation="border" size="sm" /> : "Submit Report"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
